import React, { useState, useEffect, useRef } from 'react';
import AxiosOICStat from '../AxiosOICStat';
import { Button, Row, Col, Tooltip, Checkbox, Collapse, message, Switch, Input, Modal } from 'antd';
import IndicatorSelector from '../components/IndicatorSelector';
import { useSelector } from 'react-redux';
import DatePicker from 'react-multi-date-picker';
import DatePanel from "react-multi-date-picker/plugins/date_panel"
import PresetValuesPlugin from '../components/datePickerPlugins/PresetValuesPlugin';
import Icon from "react-multi-date-picker/components/icon";
import SummaryDataTable from 'components/SummaryDataTable';
import CategoriesCollapse from 'components/CategoriesCollapse';
import * as XLSX from 'xlsx';
import { writeExcelFile } from '../utils/excelExport';
import { getIndicatorName } from '../utils/indicatorName';
import { CarryForwardCheck } from 'components/CarryForwardCheck';

export function SummaryTable({ auto_search = false }) {
    const [selectedIndicators, setSelectedIndicators] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedYears, setSelectedYears] = useState([]);
    const [searchRegionResults, setSearchRegionResults] = useState([]);
    const [searchCountryResults, setSearchCountryResults] = useState([]);
    const [searchWorldResults, setSearchWorldResults] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [loading, setLoading] = useState(false);
    const indicatorsState = useSelector((state) => state.indicators);
    const unitsState = useSelector((state) => state.units);
    const countriesState = useSelector((state) => state.countries);
    const [regionMappingsData, setRegionMappingsData] = useState([]);
    const [countryMap, setCountryMap] = useState({});
    const [selectedRegions, setSelectedRegions] = useState([]);
    const [aggregationIndicators, setAggregationIndicators] = useState([]);
    const [columns, setColumns] = useState([]);
    const [data, setData] = useState([]);
    const [fetchCompleted, setFetchCompleted] = useState(false);
    const [checkAllOIC, setCheckAllOIC] = useState(false);
    const [checkAllWorld, setCheckAllWorld] = useState(false);
    const [carryForwardCheck, setCarryForwardCheck] = useState(false);
    const [carryForwardBaseYear, setCarryForwardBaseYear] = useState('')
    const categoriesState = useSelector((state) => state.categories);
    const [keyIndicators, setKeyIndicators] = useState([]);
    const [isKeyIndicatorsFetching, setIsKeyIndicatorsFetching] = useState(false);
    const [fetchLatest, setFetchLatest] = useState(false);
    const [oicSearchTerm, setOicSearchTerm] = useState('');
    const [worldSearchTerm, setWorldSearchTerm] = useState('');


    const renderTagPlaceholder = (omittedValues) => {
        // Sort the omitted values alphabetically based on their labels
        const sortedOmittedValues = [...omittedValues].sort((a, b) => parseInt(a.value) - parseInt(b.value));

        // Create a multiline string of all sorted omitted values
        const fullList = sortedOmittedValues.map(val => val.label).join('\n');
        return (
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{fullList}</span>}>
                <span>{`+${omittedValues.length} more`}</span>
            </Tooltip>
        );
    };

    const crossCheckNotice = useRef(null);
    useEffect(() => () => crossCheckNotice.current?.destroy(), []);
    const showCrossCheckNotice = () => {
        crossCheckNotice.current = Modal.warning({
            title: 'Aggregation Calculations – Cross-Check Required',
            content: 'Please review and cross-check the aggregation calculations in the OICStat Admin Panel and report any discrepancies.',
        });
    };

    useEffect(() => {
        if (indicatorsState.status === 'succeeded') {
            AxiosOICStat.get('/indicators/descriptions').then(response => {
                let temp = [];
                response.data.forEach((ind) => {
                temp.push(ind.ind_code + " - " + getIndicatorName(ind));
                });
                setAggregationIndicators(temp);
            }
            );
        }
    }, [indicatorsState.status, indicatorsState.value]);


    useEffect(() => {
        if (auto_search) {
            handleCalculate();
        }
    }, [auto_search]);

    useEffect(() => {
        if(isKeyIndicatorsFetching === false){
            AxiosOICStat.post('/secondary-categories/get-key-indicators').then(res => {
                setKeyIndicators(res.data);
            })
            setIsKeyIndicatorsFetching(true);
        }
    }, [keyIndicators]);

    const executeCarryForward = (updatedData) => {
        const mappedData = updatedData.reduce((acc, item) => {
            const key = item.c_code;
            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(item);
          
            return acc;
          }, {});
        for(const key in mappedData){
            if(mappedData != undefined){
                if (mappedData.hasOwnProperty(key)) {
                    //console.log(`Key: ${key}`);
                    let carriedValue = null
                    let carriedInd = null
                    if(mappedData[key] != undefined){
                        mappedData[key].forEach(item => {
                            if(Number(item.d_year) >= Number(carryForwardBaseYear) && item.d_value != null){
                                carriedValue = item.d_value
                                carriedInd = item.ind_code
                            }
                            else if(Number(item.d_year) >= Number(carryForwardBaseYear) && item.d_value == null && carriedInd == item.ind_code){
                              item.d_value = carriedValue
                              item.d_date = 'Carry-Forward'
                            }
                          });
                    } 
                }
            }
        }
          
    }

    const handleCalculate = () => {
        setLoading(true);

        const missing = [];
        if(selectedYears.length == 0 && !fetchLatest){missing.push("year");}
        if (selectedIndicators.length === 0) missing.push("indicator");
        if (selectedRegions.length === 0 && selectedCountries.length === 0) missing.push("region or country");

        if (missing.length > 0) {
                    message.warning(`Please select at least one ${missing.join(", ")}.`);
                    setLoading(false);
                    return;
        }

        const formattedYears = [];
        const formattedCountries = [];
        const formattedRegions = [];
        const columnList = ["ID","Indicator", "Unit", "Year", "World"];

        selectedRegions.forEach(region => {
            formattedRegions.push(region.code);
            columnList.push(region.name);
        });

        selectedCountries.forEach(country => {
            if (country.code !== 0) { // Only push if the country code is not 0
              formattedCountries.push(country.code);
              columnList.push(country.name);
            }
          });
          

        setColumns(columnList);

        selectedYears.forEach(range => {
            if (range.length === 2) {
                for (let i = range[0].year; i <= range[1].year; i++) {
                    formattedYears.push(i);
                }
            } else {
                formattedYears.push(range[0].year);
            }
        });

        const formattedIndicators = selectedIndicators.map(indicator => indicator.split(' - ')[0]);
        var promises = [];
        if(fetchLatest){
            promises = [
                AxiosOICStat.post('/aggregations/get-latest-data', {regions: formattedRegions, indicators: formattedIndicators, first_year_range: [], last_year_range: []}),
                AxiosOICStat.post('/data/get_latest_data', { countries: formattedCountries, indicators: formattedIndicators, first_year_range: [], last_year_range: [] }),
                AxiosOICStat.post('/data/get_latest_data', { countries: ["0"], indicators: formattedIndicators, first_year_range: [], last_year_range: [] })
            ];
        }
        else{
           promises = [
                carryForwardCheck 
                    ? AxiosOICStat.post('/aggregations/calculate-carry-forward', { 
                        regions: formattedRegions, 
                        indicators: formattedIndicators, 
                        years: formattedYears,
                        base_year: carryForwardBaseYear
                    })
                    : AxiosOICStat.post('/aggregations/calculate', { 
                        regions: formattedRegions, 
                        indicators: formattedIndicators, 
                        years: formattedYears 
                    }),
                AxiosOICStat.post('/data/query', { countries: formattedCountries, indicators: formattedIndicators, years: formattedYears }),
                AxiosOICStat.post('/data/query', { countries: ["0"], indicators: formattedIndicators, years: formattedYears })
            ]; 
        }

        Promise.all(promises).then(([regionResponse, countryResponse, worldResponse]) => {
            const updatedRegionData = regionResponse.data.map((element, index) => {
                element.key = index;
                element.u_name = unitsState.value[element.u_code]?.unit_name_eng;
                if(element.d_value_last && element.d_year_last){
                    element.d_value = element.d_value_last;
                    element.d_year = element.d_year_last;
                }
                return element;
            });
            updatedRegionData.forEach(element => {
                element.r_name = regionMappingsData.find(region => region.id === element.r_code)?.region_name;
            });
            setSearchRegionResults(updatedRegionData);

            const updatedCountryData = countryResponse.data.map((element, index) => {
                element.key = index;
                element.u_name = unitsState.value[element.u_code]?.unit_name_eng;
                return element;
            });
            if(carryForwardCheck){
                executeCarryForward(updatedCountryData);
                setSearchCountryResults(updatedCountryData)
            }
            else{
                setSearchCountryResults(updatedCountryData);
            }
            const updatedWorldData = worldResponse.data.map((element, index) => {
                element.key = index;
                element.u_name = unitsState.value[element.u_code]?.unit_name_eng;
                element.c_name = "World";
                return element;
            });
            if(carryForwardCheck){
                executeCarryForward(updatedWorldData)
                setSearchWorldResults(updatedWorldData)
            }
            else{
                setSearchWorldResults(updatedWorldData);
            }
            setFetchCompleted(true); // Indicate that all data fetching is completed
            if (updatedRegionData.length > 0) showCrossCheckNotice();
        }).catch(error => {
            console.error("Error fetching data:", error);
            setLoading(false);
        });
    };

    useEffect(() => {
        if (fetchCompleted) {
            let country = Object.values(countriesState.value);
            const countryMap = new Map(country.map(item => [item.c_code, item.c_short_name_eng]));

            searchCountryResults.forEach(element => {
                const name = countryMap.get(element.c_code);
                element.c_name = name;
            });

            const allResults = [
                ...searchWorldResults,
                ...searchRegionResults,
                ...searchCountryResults,
            ];

            const countryList = [
                ...selectedRegions.map(r => r.name),
                ...selectedCountries.map(c => c.name),
                "World"
            ];

            if (fetchLatest) {
                const worldColumns = ["World Year", "World Value"];
                const otherColumns = countryList
                    .filter(name => name !== "World")
                    .flatMap(name => [`${name} Year`, `${name} Value`]);
                const transformed = transformLatestResultsToSingleRow(allResults, countryList);
                setColumns([
                    "ID", "Indicator", "Unit",
                    ...worldColumns,
                    ...otherColumns
                ]);
                setData(transformed);
                setLoading(false);
                setFetchCompleted(false);
                return;
            }

            const mapResults = (results) => {
                return results.reduce((acc, item) => {
                    const name = item.c_name || item.r_name;
                    const key = `${item.d_year}-${item.ind_code}`;

                    if (!acc[key]) {
                        acc[key] = {};
                    }
                    if (item.d_value != null) {
                        acc[key][name] = item.d_value;
                    }
                    else {
                        acc[key][name] = item.d_str_value;
                    }
                    return acc;
                }, {});
            };
 
            const mappedByName = mapResults(allResults);
            const combined_entries = [];
            let id = 0;
            for (const name in mappedByName) {
                if (mappedByName.hasOwnProperty(name)) {
                    const values = mappedByName[name];
                    const title_elements = name.split("-");
                    var year = title_elements[0];
                    const ind = title_elements[1];
                    const ind_name = getIndicatorName(indicatorsState.value[Number(ind)]);
                    const unit_code = indicatorsState.value[Number(ind)].unit_code;
                    const unit_name = unitsState.value[Number(unit_code)].unit_name_eng;
                    id++;
                    year = (year === null || year === undefined || year === "null") ? "" : year;
                    let entry = {
                        "ID": Number(ind),
                        "Indicator": ind_name,
                        "Unit": unit_name,
                        "Year": year,
                    };

                    for (const key in values) {
                        if (values.hasOwnProperty(key)) {
                            const value = values[key];
                            if (value === null || value === undefined) {
                                entry[`${key}`] = '';
                            } else if (typeof value === 'string') {
                                entry[`${key}`] = value;
                            } else {
                                entry[`${key}`] = Number(value).toFixed(3);
                            }
                        }
                    }
                    let orderedEntry = {};
                    columns.forEach(col => {
                        const columnKey = typeof col === 'object' && col.title ? col.title : col;
                        orderedEntry[columnKey] = entry[columnKey] !== undefined ? entry[columnKey] : null;
                    });
                    const selectedRegionNames = selectedRegions.map(region => region.name);
                    Object.keys(orderedEntry).forEach(key => {
                        if (selectedRegionNames.includes(key) && orderedEntry[key] === "0.000") {
                            orderedEntry[key] = null; 
                        }
                    });
                    combined_entries.push(orderedEntry);
                }
            }
            const filteredEntries = combined_entries.filter(
              (entry) => entry.Year !== undefined && entry.Year !== "undefined"
            );
            setData(filteredEntries);
            setLoading(false);
            setFetchCompleted(false);
        }
    }, [fetchCompleted, searchCountryResults, searchRegionResults, searchWorldResults, columns]);

    const transformLatestResultsToSingleRow = (allResults, countryList) => {
        const grouped = {};
        allResults.forEach(item => {
            const key = item.ind_code;
            if (!grouped[key]) {
                grouped[key] = {
                    ID: item.ind_code,
                    Indicator: getIndicatorName(indicatorsState.value[item.ind_code]) || "Unknown Indicator",
                    Unit: item.u_name,
                };
            }
            const name = item.c_name || item.r_name || "World";
            grouped[key][`${name} Year`] = item.d_year ?? "";
            grouped[key][`${name} Value`] = item.d_value ?? "";
        });

        return Object.values(grouped).map(row => {
            countryList.forEach(name => {
                if (!(name + " Value" in row)) row[name + " Value"] = "";
                if (!(name + " Year" in row)) row[name + " Year"] = "";
            });
            return row;
        });
    };

    const handleCheckAllWorldChange = (e) => {
        const isChecked = e.target.checked;
        setCheckAllWorld(isChecked);
        
        if (isChecked) {
            setSelectedCountries(countryMap.filter(country => country.c_type === 'WORLD').map(country => ({ code: country.c_code, name: country.c_short_name_eng })));
        } else {
            setSelectedCountries([]);
        }

    };

    const handleCheckAllOICChange = (e) => {
        const isChecked = e.target.checked;
        setCheckAllOIC(isChecked);
        
        if (isChecked) {
            setSelectedCountries(countryMap.filter(country => country.c_type === 'OIC').map(country => ({ code: country.c_code, name: country.c_short_name_eng })));
        } else {
            setSelectedCountries([]);
        }

    };

    const handleCheckboxChange = (e, country) => {
        if (e.target.checked) {
            setSelectedCountries([...selectedCountries, { code: country.c_code, name: country.c_short_name_eng }]);
        } else {
            setSelectedCountries(selectedCountries.filter(item => item.code !== country.c_code));
        }

        if (selectedCountries.length + (e.target.checked ? 1 : -1) === oicCountries.length) {
            setCheckAllOIC(true);
        } else {
            setCheckAllOIC(false);
        }
    };

    const exportLatestToExcel = () => {
        if (!data || data.length === 0) return;
        
        const indicatorCodes = data.map(row => row.ID);
        const indicatorObjects = indicatorCodes.map(code => {
            return Object.values(indicatorsState.value || {}).find(ind => ind.ind_code === code);
        }).filter(Boolean);
    
        const modifiedData = data.map(row => {
            const indicatorObj = indicatorObjects.find(ind => ind.ind_code === row.ID);
            const category = (indicatorObj && indicatorObj.categories && indicatorObj.categories.length > 0) 
                ? indicatorObj.categories[0] 
                : '';
        
            const newRow = {
                ID: row.ID,
                Indicator: row.Indicator,
                Category: category,
                Unit: row.Unit,
                ...Object.keys(row).reduce((acc, key) => {
                    if (!['ID', 'Indicator', 'Unit'].includes(key)) {
                        acc[key] = row[key];
                    }
                    return acc;
                }, {})
            };
        
            return newRow;
        });
    
        const headers = ['ID', 'Indicator', 'Category', 'Unit', ...columns.filter(col => !['ID', 'Indicator', 'Unit'].includes(col))];
        
        const excelData = modifiedData.map(row =>
            headers.reduce((acc, key) => {
                acc[key] = row[key] ?? "";
                return acc;
            }, {})
        );
    
        const worksheet = XLSX.utils.json_to_sheet(excelData, { header: headers });
        XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A1" });
    
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Latest Summary");
    
        writeExcelFile(workbook, "Latest_Summary.xlsx");
    };

    const exportToExcel = () => {
        const fileName = selectedIndicators.length == 1 ? `Summary - ${selectedIndicators[0].split(' - ')[1]}` : "Summary";
        const indicatorCodes = selectedIndicators.map(indicator => {
            const indCode = indicator.split(' - ')[0];
            return parseInt(indCode, 10);
        });
        const indicatorObjects = indicatorCodes.map(code => {
            return Object.values(indicatorsState.value || {}).find(ind => ind.ind_code === code);
        }).filter(Boolean);

        const modifiedData = data.map(entry => {
            const keys = Object.keys(entry);
            const updatedEntry = {};

            updatedEntry['ID'] = entry['ID'];
            updatedEntry['Indicator'] = entry['Indicator'];

            const indicatorObj = indicatorObjects.find(ind => ind.ind_code === entry['ID']);
            if (indicatorObj && indicatorObj.categories && indicatorObj.categories.length > 0) {
                updatedEntry['Category'] = indicatorObj.categories[0];
            } else {
                updatedEntry['Category'] = '';
            }

            keys.slice(2).forEach(key => {
                if (key !== 'Indicator' && key !== 'key' && key !== 'percentage') {
                    const value = entry[key];
                    if (value === null || value === undefined || value === "") {
                        updatedEntry[key] = "";
                    } else {
                        const convertedValue = Number(value);
                        updatedEntry[key] = !isNaN(convertedValue) ? convertedValue : value;
                    }
                }
            });

            return updatedEntry;
        });

        const dataHeader = Object.keys(Object.values(modifiedData)[0]);

        const worksheet = XLSX.utils.json_to_sheet(modifiedData, { header: dataHeader });     
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
        writeExcelFile(workbook, `${fileName}.xlsx`);
    };
    
    useEffect(() => {
        setSelectedCountries(selectedCountries.sort((a, b) => parseInt(a) - parseInt(b)));
        setSelectedIndicators(selectedIndicators.sort((a, b) => parseInt(a) - parseInt(b)));
    },
        [selectedIndicators, selectedYears,selectedCountries]
    );

    useEffect(() => {
        // Fetch the region mappings when the component is mounted
        const fetchRegionMappings = async () => {
            try {
                const response = await AxiosOICStat.get('/region-mappings/'); // Adjust the endpoint as needed
                setRegionMappingsData(response.data); // Assuming the response body will be the array of region mappings
            } catch (error) {
                console.error('Failed to fetch region mappings:', error);
            }
        };
        const fetchCountries = async () => {
            try{
                const response = await AxiosOICStat.get("/countries");
                setCountryMap(response.data)
            } catch (error){
                console.error('Failed to fetch country mappings:', error)
            }
        }

        fetchRegionMappings();
        fetchCountries();
    }, []);

    const regions = [
        {
            id: 1,
            label: 'Regions',
            children: (
                <Row gutter={[8, 8]}>
                    {regionMappingsData && regionMappingsData.map((region) => (
                        <Col span={6} key={region.id}>
                            <Checkbox
                                checked={selectedRegions.some(item => item.code === region.id)}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setSelectedRegions([...selectedRegions, { code: region.id, name: region.region_name }]);
                                    } else {
                                        setSelectedRegions(selectedRegions.filter(item => item.code !== region.id));
                                    }
                                }}
                                value={region.id}
                            >
                                {region.region_name}
                            </Checkbox>
                        </Col>
                    ))}
                </Row>
            )
        }
    ];
    
    const oicCountries = [
        {
            id: 2,
            label: 'OIC Countries',
            children: <Row gutter={[8, 8]}>
                <Col span={24}>
                    <Input
                        placeholder="Search OIC countries..."
                        value={oicSearchTerm}
                        onChange={(e) => setOicSearchTerm(e.target.value)}
                        style={{ marginBottom: '10px' }}
                        allowClear
                    />
                </Col>
                <Col span={24}>
                    <Checkbox
                        checked={checkAllOIC}
                        onChange={handleCheckAllOICChange}
                    >
                        Check All
                    </Checkbox>
                </Col>
                {Array.isArray(countryMap) && 
                    countryMap
                        .filter(country => country.c_type === 'OIC')
                        .filter(country => country.c_short_name_eng.toLowerCase().includes(oicSearchTerm.toLowerCase()))
                        .sort((a, b) => a.c_short_name_eng.localeCompare(b.c_short_name_eng))
                        .map((country) => (
                            <Col span={6} key={country.c_code}>
                                <Checkbox
                                    checked={selectedCountries.some(item => item.code === country.c_code)}
                                    onChange={(e) => handleCheckboxChange(e, country)}
                                    value={country.c_code}
                                >
                                    {country.c_short_name_eng}
                                </Checkbox>
                            </Col>
                        ))
                }
            </Row>
        }
    ];

    const worldCountries = [
        {
            id: 2,
            label: 'World Countries',
            children: <Row gutter={[8, 8]}>
                <Col span={24}>
                    <Input
                        placeholder="Search world countries..."
                        value={worldSearchTerm}
                        onChange={(e) => setWorldSearchTerm(e.target.value)}
                        style={{ marginBottom: '10px' }}
                        allowClear
                    />
                </Col>
                <Col span={24}>
                    <Checkbox
                        checked={checkAllWorld}
                        onChange={handleCheckAllWorldChange}
                    >
                        Check All
                    </Checkbox>
                </Col>
                {Array.isArray(countryMap) && 
                    countryMap
                        .filter(country => country.c_type === 'WORLD')
                        .filter(country => country.c_short_name_eng.toLowerCase().includes(worldSearchTerm.toLowerCase()))
                        .sort((a, b) => a.c_short_name_eng.localeCompare(b.c_short_name_eng))
                        .map((country) => (
                            <Col span={6} key={country.c_code}>
                                <Checkbox
                                    checked={selectedCountries.some(item => item.code === country.c_code)}
                                    onChange={(e) => handleCheckboxChange(e, country)}
                                    value={country.c_code}
                                >
                                    {country.c_short_name_eng}
                                </Checkbox>
                            </Col>
                        ))
                }
            </Row>
        }
    ];
      
      const exampleWidths = {
        id: 100,
        indicator: 300,
        year: 100,
        world: 150,
        d_value: 100,
        d_value_first: 100,
        d_value_last: 100
      };

    const handleCarryForward = (checked) => {
        setCarryForwardCheck(checked);
    };

    const handleYearChange = (year) => {
        setCarryForwardBaseYear(year);
    }

    const handleCategoryChange = (categoryId, isChecked) => {
        const indicatorsInCategory = Object.values(indicatorsState.value || {}).filter(
            (indicator) => indicator.cat_code === categoryId || indicator.category_codes.includes(categoryId)
        ).map((indicator) => `${indicator.ind_code} - ${getIndicatorName(indicator)}`); // Match Select items' format
    
        if (isChecked) {
            // Add category and its indicators
            if(categoryId === 32){
                const formattedKeyIndicators = keyIndicators.map(
                    (indicator) => `${indicator.ind_code} - ${getIndicatorName(indicatorsState.value[indicator.ind_code] || indicator)}`
                );
                setSelectedCategories((prev) => [...prev, categoryId]);
                setSelectedIndicators((prev) => [...new Set([...prev, ...formattedKeyIndicators])]);
            }
            else if(categoryId === 27){
                setSelectedCategories((prev) => [...prev, categoryId]);
            }
            else{
                setSelectedCategories((prev) => [...prev, categoryId]);
                setSelectedIndicators((prev) => [...new Set([...prev, ...indicatorsInCategory])]);
            }
        } else {

            if(categoryId === 32){
                const formattedKeyIndicators = keyIndicators.map(
                    (indicator) => `${indicator.ind_code} - ${getIndicatorName(indicatorsState.value[indicator.ind_code] || indicator)}`
                );
                setSelectedCategories((prev) => prev.filter((id) => id !== categoryId));
                setSelectedIndicators((prev) =>
                    prev.filter((indicator) => !formattedKeyIndicators.includes(indicator))
                );
            }
            // Remove category and its indicators
            setSelectedCategories((prev) => prev.filter((id) => id !== categoryId));
            setSelectedIndicators((prev) =>
                prev.filter((indicator) => !indicatorsInCategory.includes(indicator))
            );
        }
    };

    const handleGoalSelected = (categoryId, goals) => {
        const currentIndicators = selectedIndicators;
        const goalsArray = Array.isArray(goals) ? goals : (goals ? [goals.toString()] : []);
        
        const allSdgPatterns = Array.from({length: 17}, (_, i) => new RegExp(`^SDG\\s*0*${i+1}\\.`, 'i'));
        const allSdgIndicators = Object.values(indicatorsState.value || {}).filter(
            (indicator) => allSdgPatterns.some(pattern => pattern.test(indicator.ind_name_eng))
        ).map((indicator) => `${indicator.ind_code} - ${getIndicatorName(indicator)}`);

        const selectedPatterns = goalsArray.map(goal => new RegExp(`^SDG\\s*0*${goal}\\.`, 'i'));
        const selectedSdgIndicators = Object.values(indicatorsState.value || {}).filter(
            (indicator) => selectedPatterns.some(pattern => pattern.test(indicator.ind_name_eng))
        ).map((indicator) => `${indicator.ind_code} - ${getIndicatorName(indicator)}`);

        const filteredIndicators = currentIndicators.filter(ind => !allSdgIndicators.includes(ind));
        setSelectedIndicators([...new Set([...filteredIndicators, ...selectedSdgIndicators])]);
    };
    


    return (
        <>
            {!auto_search &&
                <Col style={{ marginBottom: '10px' }}>
                    <Collapse size="small" items={regions} bordered={false} style={{ marginBottom: "10px", width: "100%", backgroundColor: "white" }} />
                    <Collapse size="small" items={oicCountries} bordered={false} style={{ marginBottom: "10px", width: "100%", backgroundColor: "white" }} />
                    <Collapse size="small" items={worldCountries} bordered={false} style={{ marginBottom: "10px", width: "100%", backgroundColor: "white" }} />
                    <CategoriesCollapse selectedCategories={selectedCategories} onChange={handleCategoryChange} onGoalSelected={handleGoalSelected} />
                    <br />
                    <Row style={{marginBottom: '10px'}}>
                        <CarryForwardCheck isDisabled={fetchLatest} carryForwardCheck={carryForwardCheck} onYearChange={handleYearChange} onSwitchChange={handleCarryForward}></CarryForwardCheck>
                    </Row>
                    <Row style={{ marginBottom: '10px' }}>
                        <Switch
                            checked={fetchLatest}
                            onChange={setFetchLatest}
                            checkedChildren="Last Available Data"
                            unCheckedChildren="Last Available Data"
                            style={{ marginRight: 10, width: 'fit-content' }}
                        />
                    </Row>
                    <Row>
                        <IndicatorSelector
                          indicators={Object.values(indicatorsState.value || {})}
                          selectedIndicators={selectedIndicators}
                          onChange={setSelectedIndicators}
                          minWidth="500px"
                        />

                        <Button disabled={fetchLatest} shape="circle" value="large" type="primary" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <DatePicker
                            onlyYearPicker
                            multiple
                            range
                            sort
                            highlightToday={false}
                            placeholderText={"Please select a date"}
                            selected={selectedYears}
                            value={selectedYears}
                            onChange={setSelectedYears}
                            maxDate={new Date()}
                            minDate={new Date(1970, 0, 1)}
                            plugins={[
                        <DatePanel />,
                        <PresetValuesPlugin position="left" setValue={setSelectedYears} />,
                            ]}
                            render={<Icon style={{ color: 'white', width: '85%', marginTop: '2px' }} />}
                        />

                        </Button>

                        <Button type="primary" disabled={true} onClick={handleCalculate} style={{ marginLeft: '10px', }}>Search</Button>
                        <Button type="primary" disabled={false} onClick={handleCalculate} style={{ marginLeft: '10px', }}>Calculate</Button>
                    </Row>
                </Col>
            }
            <Row>
                <Col xs={24}>
                    <SummaryDataTable
                    columns={columns}
                    data={data}
                    widths={exampleWidths}
                    colYearView={true}
                    calculatePercentage={true}
                    decimalSymbol=","
                    decimalPlaces={3}
                    loading={loading}
                    />
                </Col>
            </Row>

            <Button
                onClick={fetchLatest ? exportLatestToExcel : exportToExcel}
                type="primary"
                style={{ marginLeft: '10px' }}
            >
                Export to Excel
            </Button>
        </>
    );
};